"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MessageSquare, Calendar, ArrowRight, MapPin, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  highlight?: string;
}
export default function Localseofaq() {
  const shouldReduceMotion = useReducedMotion();
  const faqData: FaqItem[] = [{
    id: "faq-1",
    question: "How can local SEO help my Plymouth business rank higher on Google Maps?",
    answer: "I optimize your complete local search ecosystem—starting with a meticulous Google Business Profile (GBP) audit, accurate primary and secondary category placement, local geo-targeted content, and high-authority local citations across Devon and the South West. By ensuring your business signals are authoritative, consistent, and active, I help you secure top rankings in the Google 3-Pack Map result.",
    highlight: "Google 3-Pack Visibility"
  }, {
    id: "faq-2",
    question: "Why should I hire an independent local SEO consultant instead of a general agency?",
    answer: "When you hire me, you work directly with Yassine Essaadi—not an entry-level account manager or an offshore junior executor. Agencies frequently dilute attention across dozens of client accounts. I maintain a selective client roster, providing customized, deep-dive local search execution tailored specifically to Plymouth's competitive environment.",
    highlight: "Direct Expert Access"
  }, {
    id: "faq-3",
    question: "How long does it take to see tangible local SEO results in Plymouth?",
    answer: "While initial Google Business Profile improvements and local citation cleanups often yield positive momentum within 4 to 8 weeks, significant and durable organic rank gains in competitive Plymouth sectors typically materialize between 3 to 6 months. I establish clear, transparent milestones right from month one.",
    highlight: "4-8 Weeks Initial Impact"
  }, {
    id: "faq-4",
    question: "What is included in your Plymouth local SEO consultancy campaign?",
    answer: "My local SEO consultancy encompasses local competitor intelligence, technical local site audits, localized content optimization (targeting Plymouth, Saltash, Plympton, and surrounding areas), NAP (Name, Address, Phone) consistency sync, Google Maps optimization, localized review strategy guidance, and transparent monthly reporting.",
    highlight: "Comprehensive Local Package"
  }, {
    id: "faq-5",
    question: "Do you handle local citation building and NAP cleanup across UK directories?",
    answer: "Yes, I conduct full manual audits of your online footprint to resolve conflicting phone numbers, outdated addresses, or duplicate Google Business Profiles. Clean, accurate NAP data across high-authority UK directories provides Google with the confidence required to trust and elevate your local business listing.",
    highlight: "Manual NAP Synchronization"
  }, {
    id: "faq-6",
    question: "Can local SEO help my Plymouth business attract clients across Devon and Cornwall?",
    answer: "Absolutely. Through structured location landing pages, strategic service-area business (SAB) configuration, and contextual regional link building, I help Plymouth businesses expand their organic reach into Exeter, Torquay, Truro, and the broader South West corridor.",
    highlight: "Regional Expansion"
  }, {
    id: "faq-7",
    question: "How do you approach geo-targeted keyword strategy for Plymouth service providers?",
    answer: "I analyze search intent and search volume specific to Plymouth sub-regions and service variants. Rather than just targeting generic terms, I target high-intent transactional phrases that local customers use when they are ready to call or hire a specialist in your trade or professional field.",
    highlight: "High-Intent Lead Keywords"
  }, {
    id: "faq-8",
    question: "Will I work directly with you, Yassine, or will my project be delegated?",
    answer: "You will work 100% directly with me. Every technical audit, strategic deliverable, keyword map, and campaign adjustment is executed by me personally. You get direct access via email, phone, and video calls with no middleman.",
    highlight: "No Middlemen"
  }, {
    id: "faq-9",
    question: "How do you track and report on local SEO performance and phone call leads?",
    answer: "I set up conversion tracking in Google Analytics 4, call tracking integration, Google Search Console, and local rank tracking. My monthly reports focus on meaningful business indicators: organic phone inquiries, directional requests, form submissions, and verified localized revenue growth.",
    highlight: "Conversion-Focused KPIs"
  }, {
    id: "faq-10",
    question: "What makes Plymouth local SEO different from national organic SEO strategies?",
    answer: "Local SEO focuses heavily on proximity, Google Maps algorithm factors, local review velocity, and regional relevance signals. National SEO focuses more on broad editorial content and nationwide domain authority. I craft a localized playbook designed to outmaneuver nearby competitors in your specific postal code.",
    highlight: "Proximity & Maps Engine"
  }, {
    id: "faq-11",
    question: "Can you assist if my local Plymouth rankings dropped following a Google core update?",
    answer: "Yes. I specialize in diagnosing local search drop-offs, identifying algorithmic penalty factors, auditing link profiles, and addressing Google Business Profile suspensions or algorithmic demotions to restore your search authority.",
    highlight: "Algorithmic Recovery"
  }, {
    id: "faq-12",
    question: "How do we begin a local SEO campaign for my Plymouth business?",
    answer: "We begin with a free initial 30-minute consultation where I examine your current search standing, identify quick-win opportunities in Plymouth, and outline a tailored roadmap. There are no high-pressure sales tactics—just practical strategic advice.",
    highlight: "Free Strategy Session"
  }];
  return <section id="localseofaq" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full max-w-7xl h-96 bg-secondary/30 blur-3xl pointer-events-none -z-10 rounded-full" />
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <Badge variant="secondary" className="mb-4 gap-1.5 px-3 py-1 text-sm rounded-full">
              <MapPin className="size-3.5 text-primary" />
              Plymouth &amp; South West Local Search Expert
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }} className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default mb-6">
            Frequently Asked Questions About{" "}
            <span className="text-primary underline decoration-accent/40 underline-offset-8">
              Plymouth Local SEO
            </span>
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: "easeOut"
        }} className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Clear, honest answers to help you understand how my direct local SEO consultancy 
            drives higher Google Maps placement, qualified inquiries, and lasting local growth in Plymouth.
          </motion.p>
        </div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 24
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2,
        ease: "easeOut"
      }} className="bg-card border border-border rounded-2xl shadow-md p-6 sm:p-8 lg:p-10 mb-16">
          <Accordion type="single" collapsible className="w-full space-y-4">
            {faqData.map((item, i) => <AccordionItem data-index={i} key={item.id} value={item.id} className="border border-border/80 rounded-xl px-4 sm:px-6 transition-[border-color,box-shadow] hover:border-primary/40 bg-background/50">
                <AccordionTrigger className="py-5 hover:no-underline text-left group">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between w-full gap-2 pr-2">
                    <span className="text-base sm:text-lg font-semibold text-primary group-hover:text-primary/80 transition-colors font-default">
                      {item.question}
                    </span>
                    {item.highlight && <Badge variant="outline" className="w-fit text-xs border-accent/40 text-accent-foreground bg-accent/10">
                        {item.highlight}
                      </Badge>}
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-5 pt-1 text-muted-foreground text-sm sm:text-base leading-relaxed border-t border-border/40 mt-1">
                  <p className="pt-2">{item.answer}</p>
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        <motion.div initial={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 0,
        y: 20
      }} whileInView={shouldReduceMotion ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.3,
        ease: "easeOut"
      }} className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 lg:p-12 shadow-xl flex flex-col lg:flex-row items-center justify-between gap-8">
          <div className="space-y-3 text-center lg:text-left max-w-2xl">
            <div className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider bg-primary-foreground/10 px-3 py-1 rounded-full text-primary-foreground">
              <CheckCircle2 className="size-4" /> Direct Consultant Access
            </div>
            <h3 className="text-2xl sm:text-3xl font-semibold tracking-tight font-default text-primary-foreground">
              Have a Specific Local SEO Question About Your Business?
            </h3>
            <p className="text-primary-foreground/90 text-sm sm:text-base leading-relaxed">
              Book a free 30-minute local search review directly with me, Yassine Essaadi. We will review your Google Business Profile and local keywords with zero sales pressure.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 w-full lg:w-auto shrink-0">
            <Button asChild size="lg" variant="secondary" className="bg-background text-foreground hover:bg-background/90 shadow-md font-medium text-base rounded-xl h-12 px-6">
              <Link to="/ContactPage">
                <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                Book Free Consultation
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 bg-transparent text-base rounded-xl h-12 px-6">
              <Link to="/CaseStudiesPage">
                View Local Results
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </section>;
}